import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useTeamMembers } from "@/hooks/use-api";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import { EmptyState } from "@/components/v2/empty-state";
import { ViewSwitcher } from "@/components/v2/view-switcher";
import { AggregateStrip } from "@/components/v2/aggregate-strip";
import { FilterToolbar } from "@/components/v2/filter-toolbar";
import { SearchHint } from "@/components/v2/search-hint";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { RepAvatar } from "@/components/v2/rep-avatar";
import { LIST_PAGE_VIEWS } from "@/pages/v2/_shared/list-page-views";
import { formatShowingRange } from "@/pages/v2/_shared/format-showing-range";

type Row = Record<string, unknown>;

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "last_name",
    header: "Name",
    cell: ({ row }) => {
      const first = row.original.first_name as string | undefined;
      const last = row.original.last_name as string | undefined;
      const fullName = [first, last].filter(Boolean).join(" ") || "—";
      const title = row.original.display_title as string | undefined;
      // §A7.1: avatar anchors the scan line (leftmost), §A7.3: 12px gap to name.
      // §A4.1: team-member rows always prefer the headshot (RepAvatar handles
      // the face-crop + monogram fallback).
      return (
        <div className="flex items-center gap-3">
          <RepAvatar
            name={fullName === "—" ? null : fullName}
            headshotUrl={row.original.headshot_url as string | null | undefined}
            size="md"
          />
          <div className="min-w-0">
            <p className="font-medium transition-colors group-hover:text-[color:var(--color-accent)]">
              {fullName}
            </p>
            {title && (
              <p className="typo-label text-[color:var(--color-text-lo)] normal-case tracking-normal">
                {title}
              </p>
            )}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "email",
    header: "Email",
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {(row.original.email as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "phone",
    header: "Phone",
    enableSorting: false,
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {(row.original.phone as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "role",
    header: "Role",
    cell: ({ row }) => <StatusBadge status={row.original.role as string} />,
  },
  {
    accessorKey: "commission_tier",
    header: "Commission Tier",
    enableSorting: false,
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {(row.original.commission_tier as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "is_active",
    header: "Status",
    enableSorting: false,
    cell: ({ row }) => {
      const active = row.original.is_active as boolean;
      return (
        <span
          className={`inline-flex items-center gap-1.5 ${active ? "text-[color:var(--color-success)]" : "text-[color:var(--color-text-lo)]"}`}
        >
          <span
            className={`size-1.5 rounded-full ${active ? "bg-[color:var(--color-success)]" : "bg-[color:var(--color-text-lo)]"}`}
          />
          {active ? "Active" : "Inactive"}
        </span>
      );
    },
  },
];

const VIEWS = LIST_PAGE_VIEWS["team-members"];

export default function V2TeamMembersPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<string>(VIEWS.defaultValue);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "last_name", desc: false },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 50;

  const { data, isLoading, isError, refetch } = useTeamMembers({
    offset: pageIndex * pageSize,
    limit: pageSize,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
  });

  const teamMembers = (data?.team_members ?? []) as Row[];
  const total = data?.total ?? 0;

  // TODO(ADV-787-followup): Active / Invited / Suspended counts need either
  // an aggregate view or role bucketing exposed by useTeamMembers. Until then
  // we render `—`.
  const aggregateItems = [
    {
      label: "Showing",
      value: formatShowingRange(teamMembers.length, pageIndex, pageSize, total),
    },
    { label: "Active", value: "—" },
    { label: "Invited", value: "—" },
    { label: "Suspended", value: "—" },
  ];

  return (
    <ListPage
      title="Team Members"
      actions={
        <>
          <SearchHint />
          <Button onClick={() => navigate("/team-members/new")}>
            Invite team member
          </Button>
        </>
      }
      tabs={
        <ViewSwitcher
          value={view}
          onValueChange={setView}
          options={[...VIEWS.options]}
          aria-label="Team Members view"
        />
      }
      filters={<FilterToolbar entity="team-members" />}
      aggregateStrip={
        view === "table" ? (
          <AggregateStrip
            items={aggregateItems}
            sort={{ label: "Name", direction: "asc" }}
          />
        ) : undefined
      }
    >
      {view === "table" && (
        <DataTable
          columns={columns}
          data={teamMembers}
          total={total}
          isLoading={isLoading}
          isError={isError}
          errorEntityLabel="team members"
          onRetry={() => refetch()}
          onRowClick={(row) => {
            const contactId = row.contact_id as string | undefined | null;
            const id = row.id as string | undefined | null;
            const target = contactId && contactId.length > 0 ? contactId : id;
            if (target) navigate(`/team-members/${target}`);
          }}
          emptyMessage="No team members yet"
          emptyDescription="Team members appear here once you invite them."
          pageSize={pageSize}
          pageIndex={pageIndex}
          onPaginationChange={setPageIndex}
          sorting={sorting}
          onSortingChange={setSorting}
        />
      )}
      {view === "analytics" && (
        <EmptyState
          title="Analytics ships in Wave 2"
          description="The Team Members analytics surface is part of the broader operational-screens plan."
        />
      )}
    </ListPage>
  );
}
